// Calendar.js - Enhanced Recovery Calendar
import { useState, useEffect, useMemo } from 'react';
import api from '../../Services/Api';
import styles from './Calendar.module.css';

function Calendar({ userData }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [logsByDate, setLogsByDate] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null); // For modal

  // User's local "today"
  const today = new Date();
  const todayString = today.toLocaleDateString('en-CA');

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const monthShort = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Fetch logs for visible month range
  useEffect(() => {
    const fetchLogs = async () => {
      try {
        setLoading(true);
        setError(null);

        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const startDate = new Date(year, month - 1, 1);
        const endDate = new Date(year, month + 2, 0);

        const startStr = startDate.toISOString().split('T')[0];
        const endStr = endDate.toISOString().split('T')[0];

        const response = await api.get(`stats/daily_logs/?start=${startStr}&end=${endStr}`);
        const logs = response.data.logs || [];

        const logsMap = {};
        logs.forEach(log => {
          logsMap[log.date] = {
            fap_count: log.fap_count,
            is_clean: log.fap_count === 0
          };
        });

        setLogsByDate(logsMap);
      } catch (err) {
        console.error('Failed to load calendar data:', err);
        setError('Failed to load calendar data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, [currentDate]);

  // Get status for a given date string
  const getDayStatus = (dateString) => {
    const log = logsByDate[dateString];
    if (!log) return 'none';
    return log.fap_count === 0 ? 'clean' : 'relapse';
  };

  // Navigation
  const goToPreviousMonth = () => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const startingDay = new Date(year, month, 1).getDay();

    const days = [];

    // Empty cells before month starts
    for (let i = 0; i < startingDay; i++) {
      days.push({ isEmpty: true });
    }

    // Actual days
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dateString = date.toLocaleDateString('en-CA');
      const status = getDayStatus(dateString);

      days.push({
        day,
        month: month,
        monthShort: monthShort[month],
        date,
        dateString,
        isEmpty: false,
        isToday: dateString === todayString,
        isFirstOfMonth: day === 1,
        status,
      });
    }

    return days;
  };

  // Handle day click - open modal
  const handleDayClick = (dayData) => {
    if (dayData.isEmpty) return;
    setSelectedDay(dayData);
  };

  // Close modal
  const closeModal = () => {
    setSelectedDay(null);
  };

  // Get day details for modal
  const getDayDetails = (dayData) => {
    if (!dayData) return null;
    
    const log = logsByDate[dayData.dateString];
    const formattedDate = dayData.date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
    
    let status, statusEmoji, statusClass, message;
    
    if (!log) {
      status = 'Not Logged';
      statusEmoji = '⚫';
      statusClass = 'none';
      message = 'No entry recorded for this day.';
    } else if (log.fap_count === 0) {
      status = 'Clean Day';
      statusEmoji = '🟢';
      statusClass = 'clean';
      message = 'Great job! You stayed clean this day.';
    } else {
      status = `${log.fap_count} Relapse${log.fap_count > 1 ? 's' : ''}`;
      statusEmoji = '🔴';
      statusClass = 'relapse';
      message = 'Remember, every setback is a setup for a comeback.';
    }
    
    return {
      formattedDate,
      status,
      statusEmoji,
      statusClass,
      message,
      isToday: dayData.isToday,
      fapCount: log?.fap_count ?? null
    };
  };

  // Calculate monthly stats
  const monthlyStats = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    let cleanDays = 0;
    let relapseDays = 0;
    let loggedDays = 0;
    
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dateString = date.toLocaleDateString('en-CA');
      const log = logsByDate[dateString];
      
      if (log) {
        loggedDays++;
        if (log.fap_count === 0) {
          cleanDays++;
        } else {
          relapseDays++;
        }
      }
    }
    
    const successRate = loggedDays > 0 ? Math.round((cleanDays / loggedDays) * 100) : 0;
    
    return { cleanDays, relapseDays, loggedDays, daysInMonth, successRate };
  }, [currentDate, logsByDate]);

  const calendarDays = generateCalendarDays();

  if (loading) {
    return (
      <div className={styles.calendarContainer}>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Loading your recovery calendar...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.calendarContainer}>
        <div className={styles.error}>
          <h3>Oops!</h3>
          <p>{error}</p>
          <button className={styles.retryButton} onClick={() => window.location.reload()}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.calendarContainer}>
      {/* Header */}
      <div className={styles.calendarHeader}>
        <div className={styles.headerContent}>
          <h1 className={styles.title}>📅 Recovery Calendar</h1>
          <p className={styles.subtitle}>
            Track your journey. Every clean day is a victory.
          </p>
        </div>
      </div>

      {/* Monthly Stats Bar */}
      <div className={styles.statsBar}>
        <div className={styles.statItem}>
          <span className={styles.statValue}>{monthlyStats.cleanDays}</span>
          <span className={styles.statLabel}>Clean Days</span>
        </div>
        <div className={styles.statDivider}></div>
        <div className={styles.statItem}>
          <span className={styles.statValue}>{monthlyStats.relapseDays}</span>
          <span className={styles.statLabel}>Relapses</span>
        </div>
        <div className={styles.statDivider}></div>
        <div className={styles.statItem}>
          <span className={`${styles.statValue} ${styles.successRate}`}>{monthlyStats.successRate}%</span>
          <span className={styles.statLabel}>Success Rate</span>
        </div>
        <div className={styles.statDivider}></div>
        <div className={styles.statItem}>
          <span className={styles.statValue}>{monthlyStats.loggedDays}/{monthlyStats.daysInMonth}</span>
          <span className={styles.statLabel}>Days Logged</span>
        </div>
      </div>

      {/* Navigation Controls */}
      <div className={styles.calendarControls}>
        <button className={styles.navButton} onClick={goToPreviousMonth}>
          <span className={styles.navIcon}>←</span>
          <span className={styles.navText}>Prev</span>
        </button>

        <div className={styles.currentMonth}>
          <h2 className={styles.monthYear}>
            {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
          </h2>
          <button className={styles.todayButton} onClick={goToToday}>
            ⦿ Today
          </button>
        </div>

        <button className={styles.navButton} onClick={goToNextMonth}>
          <span className={styles.navText}>Next</span>
          <span className={styles.navIcon}>→</span>
        </button>
      </div>

      {/* Calendar Card */}
      <div className={styles.calendarCard}>
        {/* Day names header */}
        <div className={styles.dayNames}>
          {dayNames.map((name, idx) => (
            <div key={idx} className={styles.dayName}>
              {name}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className={styles.calendarGrid}>
          {calendarDays.map((dayData, index) => (
            <div
              key={index}
              className={`${styles.calendarDay} ${
                dayData.isEmpty ? styles.empty : ''
              } ${dayData.isToday ? styles.today : ''} ${
                !dayData.isEmpty ? styles[`status${dayData.status.charAt(0).toUpperCase() + dayData.status.slice(1)}`] : ''
              }`}
              onClick={() => handleDayClick(dayData)}
              title={dayData.isEmpty ? '' : `${dayData.monthShort} ${dayData.day} - ${dayData.status === 'clean' ? 'Clean' : dayData.status === 'relapse' ? 'Relapse' : 'No log'}`}
            >
              {!dayData.isEmpty && (
                <>
                  {/* Show month on first day or if it's today */}
                  {(dayData.isFirstOfMonth || dayData.isToday) && (
                    <div className={styles.monthLabel}>{dayData.monthShort}</div>
                  )}
                  <div className={styles.dayNumber}>{dayData.day}</div>
                  
                  {/* Status indicator */}
                  <div className={styles.statusIndicator}>
                    {dayData.status === 'clean' && <span className={styles.cleanDot}>✓</span>}
                    {dayData.status === 'relapse' && (
                      <span className={styles.relapseDot}>
                        {logsByDate[dayData.dateString]?.fap_count}
                      </span>
                    )}
                    {dayData.status === 'none' && !dayData.isToday && (
                      <span className={styles.noneDot}>−</span>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className={styles.legend}>
        <div className={styles.legendItem}>
          <div className={`${styles.legendDot} ${styles.legendClean}`}></div>
          <span>Clean Day</span>
        </div>
        <div className={styles.legendItem}>
          <div className={`${styles.legendDot} ${styles.legendRelapse}`}></div>
          <span>Relapse</span>
        </div>
        <div className={styles.legendItem}>
          <div className={`${styles.legendDot} ${styles.legendNone}`}></div>
          <span>No Log</span>
        </div>
        <div className={styles.legendItem}>
          <div className={`${styles.legendDot} ${styles.legendToday}`}></div>
          <span>Today</span>
        </div>
      </div>

      {/* Tip */}
      <div className={styles.tipCard}>
        <span className={styles.tipIcon}>💡</span>
        <span className={styles.tipText}>Click on any day to see details. Log daily for accurate tracking!</span>
      </div>

      {/* Day Details Modal */}
      {selectedDay && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            {(() => {
              const details = getDayDetails(selectedDay);
              return (
                <>
                  <button className={styles.modalClose} onClick={closeModal}>×</button>
                  
                  <div className={styles.modalHeader}>
                    <span className={styles.modalEmoji}>{details.statusEmoji}</span>
                    <div className={styles.modalDate}>
                      <h3>{details.formattedDate}</h3>
                      {details.isToday && <span className={styles.todayBadge}>Today</span>}
                    </div>
                  </div>
                  
                  <div className={`${styles.modalStatus} ${styles[`modal${details.statusClass.charAt(0).toUpperCase() + details.statusClass.slice(1)}`]}`}>
                    <span className={styles.statusLabel}>Status</span>
                    <span className={styles.statusValue}>{details.status}</span>
                  </div>
                  
                  <p className={styles.modalMessage}>{details.message}</p>
                  
                  {details.fapCount !== null && details.fapCount > 0 && (
                    <div className={styles.modalStats}>
                      <div className={styles.modalStatItem}>
                        <span className={styles.modalStatValue}>{details.fapCount}</span>
                        <span className={styles.modalStatLabel}>Relapse{details.fapCount > 1 ? 's' : ''}</span>
                      </div>
                    </div>
                  )}
                  
                  <button className={styles.modalButton} onClick={closeModal}>
                    Close
                  </button>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}

export default Calendar;
